import { PAYMENTS_SECTIONS_ROUTES } from '@/models/app-internal/permissions.model';
import router from '@/router/router';
import { computed, defineComponent, onMounted } from 'vue';
import GeoMultiMethodPageHeader
  from '@/components/settings-page-header/settings-page-header.vue';
import MainSettings from '@/layouts/ps-management-page/geo-multi-method-page/main-settings/main-settings.vue';
import PaymentMethodsSettings
  from '@/layouts/ps-management-page/geo-multi-method-page/payment-methods-settings/payment-methods-settings.vue';
import AddMethodsDialog
  from '@/layouts/ps-management-page/geo-multi-method-page/add-methods-dialog/add-methods-dialog.vue';
import { storeToRefs } from 'pinia';
import { useGeoMultiMethodsStore } from '@/store/ps-management/geo-multi-methods.store';
import { useUploadImageStore } from '@/store/upload-image.store';
import { usePsManagementCountryListStore } from '@/store/ps-management/payment-methods-country-list.store';
import { ElMessageBox } from 'element-plus';
import { TagComponentTypes } from '@/models/app-common.model';

export default defineComponent({
  name: 'GeoMultiMethodPage',
  components: {
    GeoMultiMethodPageHeader,
    MainSettings,
    PaymentMethodsSettings,
    AddMethodsDialog,
  },
  setup() {
    const geoMultiMethodsStore = useGeoMultiMethodsStore();
    const {
      getGeoMultiMethod,
      createGeoMultiMethod,
      updateGeoMultiMethod,
      openMethodsAssociatingDialog,
      setDefaultGeoMultiMethodStoreState,
      getAssociatedPaymentMethodList,
      updateGeoMultiPaymentMethodEnabledState,
    } = geoMultiMethodsStore;
    const {
      geoMultiMethod,
      isGeoMultiMethodLoading,
      geoMultiMethodMainSettingsFormRef,
      isUnsavedChanges,
    } = storeToRefs(geoMultiMethodsStore);

    const { selectedCountry } = storeToRefs(usePsManagementCountryListStore());

    const multiMethodCountry = computed<string | null>(() => {
      return geoMultiMethod.value.country || selectedCountry.value?.name || null;
    });

    const uploadImageStore = useUploadImageStore();
    const { isImageUploading } = storeToRefs(uploadImageStore);

    const isSaveActionDisabled = computed<boolean>(() => {
      return isImageUploading.value ||
        isGeoMultiMethodLoading.value;
    });

    onMounted(() => {
      setDefaultGeoMultiMethodStoreState();
      const { id: multiMethodId } = router.currentRoute.value.params;
      const isCreatingMultiMethod = !multiMethodId;

      if (isCreatingMultiMethod && !selectedCountry.value) {
        router.push({ name: PAYMENTS_SECTIONS_ROUTES.PS_MANAGEMENT });

        return;
      }

      if (!isCreatingMultiMethod) {
        getGeoMultiMethod(multiMethodId as string)
          .then(() => {
            getAssociatedPaymentMethodList(multiMethodId as string);
          });
      }
    });

    const openGeoPaymentList = () => {
      if (!isUnsavedChanges.value) {
        setDefaultGeoMultiMethodStoreState();
        router.push({ name: PAYMENTS_SECTIONS_ROUTES.PS_MANAGEMENT });

        return;
      }

      ElMessageBox.confirm(
        'You have unsaved changes. Are you sure you want to leave this page?',
        'Leave page?',
        {
          confirmButtonText: 'Leave',
          cancelButtonText: 'Stay',
          type: 'warning',
        },
      ).then(() => {
        setDefaultGeoMultiMethodStoreState();
        router.push({ name: PAYMENTS_SECTIONS_ROUTES.PS_MANAGEMENT });
      });
    };

    const saveGeoMultiMethodMainSettings = () => {
      geoMultiMethodMainSettingsFormRef.value?.validate((isValid) => {
        if (!isValid) {
          return;
        }

        const isCreatingMultiMethod = !geoMultiMethod.value.multiMethodId;

        if (isCreatingMultiMethod) {
          createGeoMultiMethod(geoMultiMethod.value)
            .then((multiMethodId) => {
              if (!multiMethodId) {
                return;
              }

              getGeoMultiMethod(multiMethodId)
                .then(() => {
                  router.push({
                    name: PAYMENTS_SECTIONS_ROUTES.GEO_MULTI_METHOD_PAGE,
                    params: { id: multiMethodId },
                  });
                });
            },
            );

          return;
        }

        updateGeoMultiMethod(geoMultiMethod.value);
      });
    };

    return {
      openGeoPaymentList,
      saveGeoMultiMethodMainSettings,
      geoMultiMethod,
      isGeoMultiMethodLoading,
      isSaveActionDisabled,
      multiMethodCountry,
      openMethodsAssociatingDialog,
      TagComponentTypes,
      updateGeoMultiPaymentMethodEnabledState,
      isUnsavedChanges,
    };
  },
});
